import { createId } from '@paralleldrive/cuid2'
import {
  Committente,
  fotoInterventiSelectSchema,
  FotoIntervento,
  interventiSelectSchema,
  Intervento,
  InterventoEvento,
  Utente,
} from 'api'
import Dexie from 'dexie'
import type { EntityTable, Table } from 'dexie'
import { filter } from 'lodash-es'

import type { UnsentMarker } from '@/lib/sync-manager/helpers'
import { useAppUpdateStore } from '@/hooks/use-update-app.ts'

// import { filter } from 'lodash-es'

// import {
//   fotoInterventiSelectSchema,
//   interventiSelectSchema,
// } from '@/db/remote/schema'

/**
 * An intervento as the device stores it: the row and its history. `eventi` is
 * missing on rows pulled before the app knew events; read them with `eventsOf`.
 */
export type InterventoDoc = Intervento & { eventi?: InterventoEvento[] }

export type DexieDatabase = Dexie & {
  interventi: EntityTable<InterventoDoc & UnsentMarker, 'id'>
  foto_interventi: EntityTable<
    FotoIntervento & { blob?: File } & UnsentMarker,
    'id'
  >
  utenti: EntityTable<Utente, 'user_id'>
  committenti: EntityTable<Committente, 'user_id'>
  identity: EntityTable<{ id?: string }, 'id'>
}

const prepareSchema = (obj: Record<string, unknown>): string =>
  filter(Object.keys(obj), (p) => p !== 'id').join(', ')

// Versions 3 to 6 as they shipped. The stores were generated from the API's
// schema, so each new column would otherwise rewrite every past declaration
export const storesUpTo6 = {
  interventi:
    'id, id_interno, status, tipologia_caditoia, caditoie_num, coordinate, indirizzo, note_pre, note_post, operatore_id, data, committente_id, esito, created_at, updated_at, deleted_at, blob',
  foto_interventi:
    'id, s3_key, mime_type, intervento_id, categoria, operatore_id, committente_id, created_at, updated_at, deleted_at',
  utenti:
    'user_id, username, first_name, last_name, role, created_at, updated_at, deleted_at',
  committenti: `user_id, username, company_name`,
  identity: 'id',
  sync_meta: 'collection',
}

// Version 7 as declared. A new column on interventi or foto_interventi needs a
// new version: dexie-db.test.ts fails until the generated stores match the latest
export const storesV7 = {
  interventi:
    'id, id_interno, status, tipologia_caditoia, caditoie_num, coordinate, indirizzo, note_pre, note_post, operatore_id, data, committente_id, esito, current_operatore_id, created_at, updated_at, deleted_at, blob, _dirty',
  foto_interventi:
    'id, s3_key, mime_type, intervento_id, categoria, operatore_id, committente_id, riparazione_id, created_at, updated_at, deleted_at, _dirty',
}

/** The stores every column the API sends would get, for the guard test. */
export const generatedStores = () => ({
  interventi: `id, ${prepareSchema(interventiSelectSchema.shape)}, blob, _dirty`,
  foto_interventi: `id, ${prepareSchema(fotoInterventiSelectSchema.shape)}, _dirty`,
})

export function createDexieDb(userId: string): DexieDatabase {
  const dbName = `idromeccanicasrl-gestionale-${userId}`
  const db = new Dexie(dbName) as DexieDatabase

  const stores = storesUpTo6

  db.version(3).stores(stores)

  db.version(4)
    .stores(stores)
    .upgrade(async (tx) => {
      await tx
        .table('interventi')
        .toCollection()
        .modify((intervento: { esito?: string | null }) => {
          if (intervento.esito === 'non_impostato') {
            intervento.esito = null
          }
        })
    })

  // K23 left photos on devices that were never pushed: their updated_at is below
  // lastPush, so no sync sends them and logout doesn't warn before deleting them.
  // A photo with its file but no s3_key never reached S3; mark it changed so the
  // next sync uploads it.
  db.version(5)
    .stores(stores)
    .upgrade(async (tx) => {
      await tx
        .table('foto_interventi')
        .toCollection()
        .modify(
          (foto: {
            blob?: unknown
            s3_key?: string | null
            updated_at: Date
          }) => {
            if (foto.blob && !foto.s3_key) foto.updated_at = new Date()
          }
        )
    })

  // Unsent rows carry a marker (docs/sync/UNSENT.md), indexed, so the index
  // holds exactly the rows to push. The upgrade marks what the version before
  // would have pushed: rows changed after lastPush, and photos that never
  // reached S3 (version 5's rule, for a version 4 database upgraded at once).
  // The first sync after it pushes those once, then rows pulled from other
  // devices are no longer pushed back (K46). It writes only the marker: never
  // updated_at, which would make this device the last writer of rows it never
  // edited, so its transaction counts as the server's for the sync hooks.
  db.version(6)
    .stores({
      ...stores,
      interventi: `${stores.interventi}, _dirty`,
      foto_interventi: `${stores.foto_interventi}, _dirty`,
    })
    .upgrade(async (tx) => {
      tx.ctx = { fromServer: true }
      const markUnsent = (row: UnsentMarker) => {
        row._dirty = 1
        row._rev = createId()
      }
      for (const name of ['interventi', 'foto_interventi']) {
        const meta = await tx.table('sync_meta').get(name)
        // A lastPush that isn't a date (none yet) marks every row: pushed once
        // too many rather than not at all
        const lastPush = meta?.lastPush instanceof Date ? meta.lastPush : 0
        const table: Table = tx.table(name)
        await table.where('updated_at').above(lastPush).modify(markUnsent)
      }
      await tx
        .table('foto_interventi')
        .filter((foto) => !!foto.blob && !foto.s3_key && foto._dirty !== 1)
        .modify(markUnsent)
    })

  // Riparazioni: interventi.current_operatore_id and foto_interventi.riparazione_id,
  // indexed like every column. An intervento's events live inside it (`eventi`,
  // not indexed), so nothing to move: rows pulled before have none and read them
  // from their row (eventsOf). A version 6 app still opens this database
  db.version(7).stores({ ...stores, ...storesV7 })

  // A newer version of the app opened this database in another tab (upgrading
  // it): Dexie closes this connection and opens it again on this tab's schema
  // at its next operation. The tab reloads onto the new code at its next page
  // change, as when the service worker finds a new build (router.ts)
  db.on('versionchange', (event) => {
    if (event.newVersion)
      useAppUpdateStore.setState({ hadUpdateAt: new Date() })
  })

  // @ts-expect-error make available for debugging
  if (typeof window !== 'undefined') window.db = db

  return db
}
